import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { taskAPI, stageGateAPI } from '../../../api/services';
import StageGateDialog from './StageGateDialog';

// ─── Constants ──────────────────────────────────────────────
const COLUMNS = ['todo', 'in_progress', 'blocked', 'review', 'done'] as const;
const COL_LABELS: Record<string, string> = { todo: 'To Do', in_progress: 'In Progress', blocked: 'Blocked', review: 'Review', done: 'Done' };
const COL_HEADER_BG: Record<string, string> = { todo: 'bg-gray-50', in_progress: 'bg-blue-50', blocked: 'bg-red-50', review: 'bg-yellow-50', done: 'bg-green-50' };
const STATUS_DOT: Record<string, string> = { todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500', review: 'bg-yellow-500', done: 'bg-green-500' };
const PRIORITY_PILL: Record<string, string> = { critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white', medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600' };

// ─── Draggable cell ─────────────────────────────────────────
const DraggableCell: React.FC<{ dragId: string; children: React.ReactNode }> = ({ dragId, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: dragId });
  const style: React.CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1, position: 'relative', zIndex: isDragging ? 50 : undefined }
    : {};
  return <div ref={setNodeRef} style={style} {...listeners} {...attributes}>{children}</div>;
};

// ─── Droppable cell ─────────────────────────────────────────
const DroppableCell: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`overflow-hidden transition-colors ${isOver ? 'bg-blue-50/60' : ''}`}>{children}</div>;
};

// ─── Types ──────────────────────────────────────────────────
interface StageGateConfig {
  stage: string;
  gateType: 'approval' | 'testing';
  mandatory: boolean;
  defaultApprovers?: string[];
}

interface Props {
  projectId: string;
  tasks: any[];
  features: any[];
  onRefresh: () => void;
  onTaskClick?: (taskId: string) => void;
  stageGates?: StageGateConfig[];
  projectMembers?: { userId: string; name: string }[];
}

type FeatureSection = {
  id: string;
  title: string;
  taskCount: number;
  sp: number;
  tasksByCol: Record<string, any[]>;
};

// ─── Component ──────────────────────────────────────────────
const WorkspaceBoard: React.FC<Props> = ({ projectId, tasks, features, onRefresh, onTaskClick, stageGates = [], projectMembers = [] }) => {
  const navigate = useNavigate();
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [colOverrides, setColOverrides] = useState<Record<string, string>>({});
  const [gateDialog, setGateDialog] = useState<{ taskId: string; taskTitle: string; targetCol: string; gates: StageGateConfig[] } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const toggleFeature = (fId: string) => {
    setExpandedFeatures(prev => {
      const n = new Set(prev); n.has(fId) ? n.delete(fId) : n.add(fId); return n;
    });
  };

  // ─── Build feature sections ─────────────────────────────
  const { featureSections, standaloneByCol, itemLookup } = useMemo(() => {
    const sections: FeatureSection[] = [];
    const stByCol: Record<string, any[]> = {};
    for (const col of COLUMNS) stByCol[col] = [];
    const lookup = new Map<string, { item: any; col: string; type: string; featureId?: string }>();

    const featureMap: Record<string, any> = {};
    for (const f of features) featureMap[f._id] = f;

    // Apply overrides to tasks
    const effectiveTasks = tasks.map(t => ({
      ...t,
      _col: colOverrides[t._id] || t.status || 'todo',
    }));

    // Group by feature
    const featureGroups: Record<string, { feature: any; tasks: any[] }> = {};
    const standalone: any[] = [];

    for (const task of effectiveTasks) {
      if (task.parentType === 'feature' && task.parentId && featureMap[task.parentId]) {
        if (!featureGroups[task.parentId]) featureGroups[task.parentId] = { feature: featureMap[task.parentId], tasks: [] };
        featureGroups[task.parentId].tasks.push(task);
      } else {
        standalone.push(task);
      }
    }

    // Features with no tasks
    for (const f of features) {
      if (!featureGroups[f._id]) featureGroups[f._id] = { feature: f, tasks: [] };
    }

    // Build sections
    for (const [fId, group] of Object.entries(featureGroups)) {
      const tasksByCol: Record<string, any[]> = {};
      for (const col of COLUMNS) tasksByCol[col] = [];
      for (const task of group.tasks) {
        if (tasksByCol[task._col]) tasksByCol[task._col].push(task);
        lookup.set(task._id, { item: task, col: task._col, type: 'task', featureId: fId });
      }
      const taskSP = group.tasks.reduce((s: number, t: any) => s + (t.storyPoints || 0), 0);
      sections.push({
        id: fId,
        title: group.feature.name || group.feature.title,
        taskCount: group.tasks.length,
        sp: Math.max(taskSP, group.feature.storyPoints || 0),
        tasksByCol,
      });
    }

    for (const task of standalone) {
      if (stByCol[task._col]) stByCol[task._col].push(task);
      lookup.set(task._id, { item: task, col: task._col, type: 'task' });
    }

    return { featureSections: sections, standaloneByCol: stByCol, itemLookup: lookup };
  }, [tasks, features, colOverrides]);

  // ─── Drag handlers ──────────────────────────────────────
  const handleDragStart = (e: DragStartEvent) => setActiveDragId(e.active.id as string);

  const handleDragEnd = useCallback(async (e: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over) return;

    const dragId = active.id as string;
    const targetCol = (over.id as string).split(':')[0];
    if (!(COLUMNS as readonly string[]).includes(targetCol)) return;

    const entry = itemLookup.get(dragId);
    if (!entry || entry.col === targetCol) return;

    const matchingGates = stageGates.filter(g => g.stage === targetCol);
    if (matchingGates.length > 0) {
      // Check if all gates for this task+stage are already cleared
      try {
        const existingRes = await stageGateAPI.getTaskGates(dragId);
        const existingGates = existingRes.data?.data || [];
        const stageGatesForTarget = existingGates.filter((g: any) => g.stage === targetCol);
        const allCleared = stageGatesForTarget.length > 0 && stageGatesForTarget.every((g: any) =>
          g.status === 'approved' || g.status === 'passed' || g.status === 'skipped'
        );
        if (!allCleared) {
          setGateDialog({
            taskId: dragId,
            taskTitle: entry.item?.title || 'Task',
            targetCol,
            gates: matchingGates,
          });
          return;
        }
      } catch {
        setGateDialog({
          taskId: dragId,
          taskTitle: entry.item?.title || 'Task',
          targetCol,
          gates: matchingGates,
        });
        return;
      }
    }

    // Optimistic
    setColOverrides(prev => ({ ...prev, [dragId]: targetCol }));

    // Fire API in background, then silently refresh
    taskAPI.updateStatus(dragId, targetCol)
      .catch(() => {
        setColOverrides(prev => { const n = { ...prev }; delete n[dragId]; return n; });
      })
      .finally(() => {
        onRefresh();
        setTimeout(() => setColOverrides({}), 100);
      });
  }, [itemLookup, onRefresh, stageGates]);

  const completeGatedMove = useCallback((taskId: string, targetCol: string) => {
    setColOverrides(prev => ({ ...prev, [taskId]: targetCol }));
    taskAPI.updateStatus(taskId, targetCol)
      .catch(() => {
        setColOverrides(prev => { const n = { ...prev }; delete n[taskId]; return n; });
      })
      .finally(() => {
        onRefresh();
        setTimeout(() => setColOverrides({}), 100);
      });
  }, [onRefresh]);

  // ─── Render task row ────────────────────────────────────
  const renderTask = (task: any, indented: boolean) => (
    <DraggableCell dragId={task._id}>
      <div
        className={`group/row px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-50 transition-colors border-b border-gray-100 ${indented ? 'pl-5' : ''}`}
        onClick={() => onTaskClick ? onTaskClick(task._id) : navigate(`/employee/tasks/${task._id}`)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[task._col] || STATUS_DOT[task.status] || 'bg-gray-300'}`} />
          <span className={`text-sm truncate flex-1 ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`} title={task.title}>
            {task.title}
          </span>
          {task.priority && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${PRIORITY_PILL[task.priority] || 'bg-gray-200 text-gray-600'}`}>
              {task.priority}
            </span>
          )}
          {(task.storyPoints ?? 0) > 0 && (
            <span className="text-xs text-blue-600 font-medium shrink-0">{task.storyPoints}</span>
          )}
          {(task.assignedTo ?? []).length > 0 && (
            <div className="flex -space-x-1 shrink-0">
              {(task.assignedTo ?? []).slice(0, 2).map((a: any, i: number) => (
                <div key={i} className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center border border-white" title={a.name}>
                  {(a.name || '?')[0]}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DraggableCell>
  );

  const getDragOverlayContent = () => {
    if (!activeDragId) return null;
    const entry = itemLookup.get(activeDragId);
    if (!entry) return null;
    return (
      <div className="bg-white border border-blue-300 px-3 py-2 shadow-lg rotate-1 min-w-[200px]">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[entry.col] || 'bg-gray-300'}`} />
          <span className="text-sm text-gray-800 truncate">{entry.item?.title}</span>
        </div>
      </div>
    );
  };

  const hasStandalone = COLUMNS.some(c => (standaloneByCol[c] || []).length > 0);

  return (
    <div className="flex-1 overflow-auto">
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="min-w-[900px]">
          {/* Column headers */}
          <div className="grid sticky top-0 z-10 border-b border-gray-200" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
            {COLUMNS.map(col => (
              <div key={col} className={`px-3 py-2.5 ${COL_HEADER_BG[col]} border-r border-gray-200 last:border-r-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${STATUS_DOT[col]}`} />
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{COL_LABELS[col]}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {featureSections.length === 0 && !hasStandalone && (
            <div className="text-center py-12 text-sm text-gray-400">No tasks yet.</div>
          )}

          {/* Feature sections */}
          {featureSections.map((section, idx) => {
            const isExpanded = expandedFeatures.has(section.id);
            const bandBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/70';

            return (
              <div key={section.id} className={`border-b border-gray-200 ${bandBg}`}>
                {/* Feature header — centered name */}
                <div className={`border-l-2 border-blue-400 relative ${isExpanded ? 'py-2 bg-blue-50' : 'py-1.5'}`}>
                  <div className="flex items-center justify-center">
                    {isExpanded ? (
                      <span className="text-sm font-medium text-blue-700 cursor-pointer hover:underline truncate max-w-[60%] text-center" onClick={() => navigate(`/employee/projects/${projectId}?view=feature-detail&featureId=${section.id}`)}>
                        {section.title}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 truncate max-w-[60%] text-center" title={section.title}>
                        {section.title}
                      </span>
                    )}
                  </div>
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                    <button onClick={() => toggleFeature(section.id)} className="shrink-0">
                      <svg className={`w-3 h-3 text-blue-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </button>
                    <span title={section.title} className="cursor-help"><svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg></span>
                  </div>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <span className="text-xs text-gray-400">{section.taskCount}</span>
                    {section.sp > 0 && <span className="text-xs text-blue-600 font-medium">{section.sp} SP</span>}
                  </div>
                </div>

                {/* Task grid */}
                {isExpanded && (
                  <div className="grid" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
                    {COLUMNS.map(col => (
                      <DroppableCell key={col} id={`${col}:${section.id}`}>
                        <div className="border-r border-gray-100 last:border-r-0 min-h-[8px]">
                          {(section.tasksByCol[col] || []).map((task: any) => (
                            <div key={task._id}>{renderTask(task, true)}</div>
                          ))}
                        </div>
                      </DroppableCell>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Standalone tasks */}
          {hasStandalone && (
            <div className="border-b border-gray-200">
              {featureSections.length > 0 && (
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Other tasks</span>
                </div>
              )}
              <div className="grid" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, 1fr)` }}>
                {COLUMNS.map(col => (
                  <DroppableCell key={col} id={`${col}:standalone`}>
                    <div className="border-r border-gray-100 last:border-r-0 min-h-[8px]">
                      {(standaloneByCol[col] || []).map((task: any) => (
                        <div key={task._id}>{renderTask(task, false)}</div>
                      ))}
                    </div>
                  </DroppableCell>
                ))}
              </div>
            </div>
          )}
        </div>

        <DragOverlay dropAnimation={null}>
          {getDragOverlayContent()}
        </DragOverlay>
      </DndContext>

      {gateDialog && (
        <StageGateDialog
          taskId={gateDialog.taskId}
          taskTitle={gateDialog.taskTitle}
          projectId={projectId}
          targetStage={gateDialog.targetCol}
          gates={gateDialog.gates}
          projectMembers={projectMembers}
          onSubmitted={() => {
            completeGatedMove(gateDialog.taskId, gateDialog.targetCol);
            setGateDialog(null);
          }}
          onSkip={() => {
            completeGatedMove(gateDialog.taskId, gateDialog.targetCol);
            setGateDialog(null);
          }}
          onClose={() => setGateDialog(null)}
        />
      )}
    </div>
  );
};

export default WorkspaceBoard;
